import { Component, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { VideoProvider } from "@/components/VideoModal";
import { AudioProvider } from "@/components/AudioProvider";
import InvoicePage from "@/pages/InvoicePage";
import ChatWidget from "@/components/ChatWidget";
import DisclaimerGate from "@/components/DisclaimerGate";
import PageBackground from "@/components/PageBackground";
import Home from "@/pages/Home";
import Product from "@/pages/Product";
import Team from "@/pages/Team";
import Admin from "@/pages/Admin";
import Certification from "@/pages/Certification";
import Whitepaper from "@/pages/Whitepaper";
import Portal from "@/pages/Portal";
import Education from "@/pages/Education";
import JoinOurTeam from "@/pages/JoinOurTeam";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#fafafa] px-6 text-center">
          <p className="font-display text-3xl">Something went off-script</p>
          <p className="max-w-sm text-sm text-[#56636b]">An unexpected error occurred. Please refresh the page.</p>
          <button
            onClick={() => window.location.reload()}
            data-testid="error-reload-button"
            className="bg-[#0a0a0a] px-6 py-3 text-sm text-white"
          >
            Refresh
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <ErrorBoundary>
      <AudioProvider>
      <VideoProvider>
        <BrowserRouter>
          <ScrollToTop />
          <PageBackground />
          <div className="relative z-10">
          <Nav />
          <main className="min-h-screen">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/products/:slug" element={<Product />} />
              <Route path="/team" element={<Team />} />
              <Route path="/certification" element={<Certification />} />
              <Route path="/education" element={<Education />} />
              <Route path="/careers" element={<JoinOurTeam />} />
              <Route path="/whitepaper" element={<Whitepaper />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/invoice/:id" element={<InvoicePage />} />
              <Route path="/crm" element={<Portal />} />
              <Route
                path="*"
                element={
                  <div className="flex min-h-[70vh] flex-col items-center justify-center gap-4 px-6 pt-24 text-center">
                    <p className="font-lab text-xs uppercase tracking-[0.2em] text-[#0e8c8c]">404</p>
                    <p className="font-display text-4xl">This page does not exist</p>
                    <a href="/" data-testid="notfound-home-link" className="border border-[#0a0a0a] px-6 py-3 text-sm">
                      Back to home
                    </a>
                  </div>
                }
              />
            </Routes>
          </main>
          <Footer />
          <ChatWidget />
          <DisclaimerGate />
          </div>
        </BrowserRouter>
        <Toaster position="bottom-left" richColors />
      </VideoProvider>
      </AudioProvider>
    </ErrorBoundary>
  );
}